import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import FileCell from "@/components/FileCell";

// column shape:
// { key, label, type: text|select|date|time|checkbox|number|customer|owner|priority,
//   options?: string[], width?, placeholder? }

export default function ItemsTable({ endpoint, columns, defaultItem, testIdPrefix, extraFilter, emptyLabel = "No items yet.", rowActions }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ ...defaultItem });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      let list = data || [];
      if (extraFilter) list = list.filter(extraFilter);
      setItems(list);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const addItem = async () => {
    // Validate at least one required-ish field
    const requiredKey = columns.find((c) => c.required)?.key;
    if (requiredKey && !newItem[requiredKey]) {
      toast.error(`${columns.find((c) => c.key === requiredKey).label} is required`);
      return;
    }
    try {
      await api.post(`/${endpoint}`, newItem);
      setNewItem({ ...defaultItem });
      toast.success("Added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateItem = async (id, patch) => {
    try {
      await api.patch(`/${endpoint}/${id}`, patch);
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch } : it));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/${endpoint}/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const renderCell = (col, item, isNew = false) => {
    const value = isNew ? newItem[col.key] : item[col.key];
    const setValue = (v) => {
      if (isNew) setNewItem((p) => ({ ...p, [col.key]: v }));
      else updateItem(item.id, { [col.key]: v });
    };
    const tid = `${testIdPrefix}-${isNew ? "new" : item.id}-${col.key}`;

    if (col.type === "select") {
      return (
        <Select value={value || undefined} onValueChange={setValue}>
          <SelectTrigger data-testid={tid} className="bg-background h-10 w-full text-sm">
            <SelectValue placeholder={col.placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {(col.options || []).map((opt) => {
              const label = typeof opt === "string" ? opt : opt.label;
              const val = typeof opt === "string" ? opt : opt.value;
              const icon = typeof opt === "object" ? opt.icon : null;
              return (
                <SelectItem key={val} value={val} className="text-sm">
                  {icon ? (
                    <span className="inline-flex items-center gap-2">
                      <img src={icon} alt="" className="h-4 w-4 object-contain shrink-0" />
                      <span>{label}</span>
                    </span>
                  ) : label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    }

    if (col.type === "checkbox") {
      return (
        <div className="flex justify-center">
          <Checkbox
            checked={!!value}
            onCheckedChange={(v) => setValue(!!v)}
            data-testid={tid}
          />
        </div>
      );
    }

    if (col.type === "date" || col.type === "time" || col.type === "number") {
      return (
        <Input
          type={col.type}
          value={value ?? ""}
          onChange={(e) => setValue(col.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
          onBlur={() => {}}
          data-testid={tid}
          className="bg-background h-10 text-sm"
        />
      );
    }

    if (col.type === "file") {
      return <FileCell value={value} onChange={setValue} testId={tid} />;
    }

    // text
    return (
      <Input
        type="text"
        value={value ?? ""}
        onChange={(e) => {
          if (isNew) setNewItem((p) => ({ ...p, [col.key]: e.target.value }));
          else setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, [col.key]: e.target.value } : it));
        }}
        onBlur={(e) => { if (!isNew) updateItem(item.id, { [col.key]: e.target.value }); }}
        placeholder={col.placeholder}
        data-testid={tid}
        title={value ?? ""}
        className="bg-background h-10 text-sm"
      />
    );
  };

  return (
    <Card className="card-elev">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead
                    key={c.key}
                    className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground py-4 px-3 whitespace-nowrap"
                    style={{ width: c.width, minWidth: c.width || 160 }}
                  >
                    {c.label}
                  </TableHead>
                ))}
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* New row */}
              <TableRow className="bg-muted/40 hover:bg-muted/50" data-testid={`${testIdPrefix}-new-row`}>
                {columns.map((c) => (
                  <TableCell key={c.key} className="py-2.5 px-3 align-middle">{renderCell(c, null, true)}</TableCell>
                ))}
                <TableCell className="py-3 px-2">
                  <Button size="icon" variant="ghost" onClick={addItem} data-testid={`${testIdPrefix}-add-btn`}
                    className="h-9 w-9 text-sage hover:text-sage hover:bg-sage/10">
                    <Plus className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>

              {loading && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              )}

              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} className="text-center py-10 text-muted-foreground text-sm">
                    {emptyLabel}
                  </TableCell>
                </TableRow>
              )}

              {items.map((item) => (
                <TableRow key={item.id} data-testid={`${testIdPrefix}-row-${item.id}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className="py-2.5 px-3 align-middle">{renderCell(c, item)}</TableCell>
                  ))}
                  <TableCell className="py-3 px-2">
                    <div className="flex items-center gap-1 justify-end">
                      {rowActions && rowActions(item)}
                      <Button size="icon" variant="ghost" onClick={() => deleteItem(item.id)}
                        data-testid={`${testIdPrefix}-delete-${item.id}`}
                        className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
